<script setup lang="ts">
/**
 * 颜色控件：色块 + 十六进制输入；点色块展开**一体的拾色面板**。
 *
 * 无论有没有透明度，交互都只有这一条路径 —— 点色块，面板里调完。
 * 颜色和透明度在同一个面板里，不存在「点开一层、再点开一层」。
 *
 * 色块本身画出 alpha：底下是棋盘格，`<i>` 的透明度就是当前 alpha，
 * 所以不打开面板也能一眼看出「这个颜色是半透明的」。
 */
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import type { ColorWidget } from '../../types/widget'
import { alphaOf, isHexColor, toRgbHex, withAlpha } from '../../types/color'
import NpColorPickerPanel from './NpColorPickerPanel.vue'

const props = defineProps<{ widget: ColorWidget }>()
const emit = defineEmits<{ (e: 'update', value: string): void }>()

/**
 * 色块上那层纯色（棋盘格上盖的那块）。
 *
 * ⚠️ 解析不出颜色（空值 / 手输到一半）时兜底的不是「黑色」，而是
 * **「这个控件现在没有颜色」** —— 所以它得是令牌，不能写死 `#000000`：
 * 浅色主题下那块黑看起来像渲染错误。令牌定义在本组件的 `.np-widget--color` 上
 * （色块本身的样式在 `theme.css`），和 `--np-swatch-alpha` 是一对。
 */
const swatchColor = computed(() => toRgbHex(props.widget.value) || 'var(--np-swatch-empty)')
const swatchAlpha = computed(() => alphaOf(props.widget.value))

/* ── 面板开关 ───────────────────────────────────────────────── */

const open = ref(false)
const swatchEl = ref<HTMLElement | null>(null)
const panelEl = ref<HTMLElement | null>(null)
const pos = ref({ x: 0, y: 0 })

/**
 * 画布缩放比 —— 面板要跟着放大缩小，不然缩放到 200% 时
 * 它还是那么小，看起来跟节点完全脱节。
 *
 * 怎么算出来的：**视觉尺寸 ÷ 布局尺寸**。
 *   getBoundingClientRect() 会被祖先的 transform 影响 → 是放大后的
 *   offsetHeight 不受 transform 影响 → 是布局尺寸
 * 两者一比就是缩放比。
 *
 * 这样做的好处是**不依赖具体图引擎** —— 不需要知道 Vue Flow 的
 * 类名或 viewport 结构，换成别的引擎（或者根本没有引擎）也成立。
 */
const canvasScale = ref(1)

/* ── 摆放 ───────────────────────────────────────────────────── */

/** 面板与色块之间的间隙（屏幕像素） */
const PANEL_GAP = 6
const VIEW_MARGIN = 8

/**
 * 摆放面板：优先在色块**下方**，下方放不下就**翻到上方**。
 *
 * ⚠️ 之前是把位置硬夹回可见范围，结果很别扭：面板被画布缩放放大之后很高，
 * 或者色块本来就靠屏幕下方 —— 硬夹会把面板**往上推**贴着色块，
 * 看起来就像「位置算错了」。上下翻转才是预期行为。
 *
 * 缩放比在这里是间接用上的：`getBoundingClientRect()` 量到的是**放大后**的
 * 尺寸，所以翻转判断和横向夹取都是按真实视觉占位算的。
 */
function positionPanel() {
  const anchor = swatchEl.value?.getBoundingClientRect()
  if (!anchor) return

  const size = panelEl.value?.getBoundingClientRect()
  const maxX = size ? window.innerWidth - size.width - VIEW_MARGIN : Infinity
  const maxY = size ? window.innerHeight - size.height - VIEW_MARGIN : Infinity

  const x = Math.max(VIEW_MARGIN, Math.min(anchor.left, maxX))

  let y = anchor.bottom + PANEL_GAP
  if (y > maxY) {
    const above = anchor.top - PANEL_GAP - (size?.height ?? 0)
    // 上面也放不下（面板比可视区还高）→ 只能贴顶
    y = above >= VIEW_MARGIN ? above : VIEW_MARGIN
  }

  pos.value = { x, y }
}

/*
 * ── 跟随画布的平移 / 缩放 ─────────────────────────────────────
 *
 * 面板 Teleport 到 body（不然会被画布的 overflow: hidden 裁掉），
 * 位置是**打开那一刻**算的 —— 之后画布缩放或拖动，面板就留在原地不动了。
 *
 * 用 rAF 轮询色块的位置，而不是让宿主把「画布变了」通知进来：
 * 一个颜色控件不该反过来要求集成方喂视图状态。
 * 只有面板打开时才跑，每帧一次 getBoundingClientRect。
 */
let rafId = 0
let lastRectKey = ''

function trackAnchor() {
  if (!open.value) return
  const el = swatchEl.value

  if (el) {
    const rect = el.getBoundingClientRect()

    // 缩放比变了 → 面板尺寸跟着变
    const layout = el.offsetHeight || 1
    const nextScale = rect.height / layout
    if (Math.abs(nextScale - canvasScale.value) > 0.001) {
      canvasScale.value = nextScale
    }

    const key = `${rect.left}|${rect.top}|${rect.width}|${rect.height}`
    if (key !== lastRectKey) {
      lastRectKey = key
      positionPanel()
      // 面板刚渲染出来时量不到尺寸，等渲染完再摆一次（这次才有得翻转）
      nextTick(positionPanel)
    }
  }

  rafId = requestAnimationFrame(trackAnchor)
}

function stopTracking() {
  cancelAnimationFrame(rafId)
  rafId = 0
}

/**
 * 色块被按下。
 *
 * ⚠️ **在 `pointerdown` 上打开，而不是 `click`**：触摸端上，节点行 / 画布
 * 的拖拽逻辑会接管这次触摸（`touch-action: none` + pointer capture），
 * 于是浏览器**根本不发 `click`** —— 表现就是"移动端颜色控件点不动"
 * （真机反馈）。鼠标上两者等价，所以统一走这一条。
 *
 * ⚠️ `preventDefault()` 是为了别让这一下同时被当成"按在行上"（会起拖拽 /
 * 聚焦输入框）。文档级的关闭监听按 `.np-widget__swatch` 放过它（见
 * `onDocPointerDown`），所以不会"刚开就关"。
 */
function onSwatchDown(ev: PointerEvent) {
  ev.preventDefault()
  toggle()
}

function toggle() {
  if (open.value) {
    open.value = false
    return
  }
  const rect = swatchEl.value?.getBoundingClientRect()
  if (!rect) return
  positionPanel()
  open.value = true
  // 头一帧会重新量一次，那时面板已经渲染出来，可以判断要不要翻转
  lastRectKey = ''
  cancelAnimationFrame(rafId)
  rafId = requestAnimationFrame(trackAnchor)
}

/**
 * 点别处 → 关掉弹层。
 *
 * ⚠️ 放过两类目标：
 *   · 色块自己（`onSwatchDown` 就是"再点一下关掉"，不能在这儿先关了）；
 *   · **弹层内部**（在拾色器里点 / 拖 / 滑，都不该把它关掉）。
 *
 * ⚠️ 弹层的根类名是 `.np-color-popover`（本组件模板里那个 div），
 * 里面的拾色面板叫 `.np-colorpicker` —— 原来这里写的是 `.np-picker`，
 * **两个都不匹配**：于是移动端上"手指一按拾色器，弹层立刻被关掉"
 * （真机反馈），鼠标上因为拖动多半落在别处而没暴露。
 */
function onDocPointerDown(ev: PointerEvent) {
  if (!open.value) return
  const target = ev.target as HTMLElement | null
  if (
    target?.closest('.np-color-popover') ||
    target?.closest('.np-colorpicker') ||
    target?.closest('.np-widget__swatch')
  ) {
    return
  }
  open.value = false
}

function onEscape(ev: KeyboardEvent) {
  if (ev.key !== 'Escape' || !open.value) return
  // capture 阶段拦下，别让画布也响应
  ev.stopPropagation()
  open.value = false
}

watch(open, (isOpen) => {
  if (isOpen) {
    document.addEventListener('pointerdown', onDocPointerDown, true)
    window.addEventListener('keydown', onEscape, true)
  } else {
    document.removeEventListener('pointerdown', onDocPointerDown, true)
    window.removeEventListener('keydown', onEscape, true)
    stopTracking()
  }
})

// 缩放变了 → 面板尺寸变了 → 重新摆一次（可能要翻转）
watch(canvasScale, () => {
  if (open.value) nextTick(positionPanel)
})

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onDocPointerDown, true)
  window.removeEventListener('keydown', onEscape, true)
  stopTracking()
})

/* ── 行内十六进制输入 ───────────────────────────────────────── */

function onType(ev: Event) {
  const input = ev.target as HTMLInputElement
  const raw = input.value.trim()
  if (!isHexColor(raw)) {
    // 手输错一个字符很常见，弹红框比默默还原更烦人
    input.value = props.widget.value
    return
  }
  // 走 withAlpha 归一化：满不透明退回 6 位，不拼多余的 ff
  emit('update', withAlpha(raw, alphaOf(raw)))
}
</script>

<template>
  <span class="np-widget np-widget--color nodrag" :title="props.widget.hint">
    <button
      ref="swatchEl"
      class="np-widget__swatch is-button"
      type="button"
      :disabled="props.widget.disabled"
      :title="`${props.widget.value} · 点击调整颜色${props.widget.alpha ? '与透明度' : ''}`"
      :style="{ '--np-swatch-alpha': swatchAlpha }"
      @pointerdown.stop="onSwatchDown"
      @click.stop
    >
      <i :style="{ background: swatchColor }" />
    </button>

    <input
      class="np-widget__field np-widget__field--hex"
      type="text"
      autocomplete="off"
      spellcheck="false"
      :value="props.widget.value"
      :disabled="props.widget.disabled"
      @change="onType"
    />

    <Teleport to="body">
      <div
        v-if="open"
        ref="panelEl"
        class="np-color-popover"
        :style="{
          left: `${pos.x}px`,
          top: `${pos.y}px`,
          // 跟着画布缩放；从左上角放大，锚点那一角才不会跑
          transform: `scale(${canvasScale})`,
          transformOrigin: 'top left',
        }"
        @pointerdown.stop
        @contextmenu.prevent
      >
        <NpColorPickerPanel
          :value="props.widget.value"
          :alpha="props.widget.alpha"
          @update="emit('update', $event)"
        />
      </div>
    </Teleport>
  </span>
</template>

/* ══════════════════════════════════════════════════════════════════
   颜色的**弹出层**（从 `theme.css` 向下拆到这里）

   ⚠️ 同批删掉了一份**重复的 `.np-widget__swatch`**：那套规则在
   `theme.css` 里出现了**两次**（尺寸 fallback 一个 17px、一个 18px，
   后者静默胜出），把前一份写得更详细的注释（解释「为什么必须显式给
   background-color，否则 buttonface 会透在棋盘格里」）整个废掉了。
   现在只保留原来那一份（在 `theme.css` 的「颜色：色块」段）。
   ══════════════════════════════════════════════════════════════════ */

<style>
/*
 * 弹出层的皮肤一律走 `--np-popup-*` + 面板族的文字色。
 *
 * ⚠️ **为什么必须是这两族**：面板 Teleport 到 body（不然会被画布的
 * `overflow: hidden` 裁掉），于是它**不是 `.np-designer` 的后代** ——
 * 声明在 `.np-designer, .np-node` 上的令牌（`--np-text` / `--np-widget-*`
 * / `--np-accent`…）它一个都继承不到。`--np-popup-*` 和 `--np-panel-*`
 * 都声明在 `:root`，而且配方（`data-np-popup='glass' / 'outline'`，
 * 见 recipes.css）改的就是 `--np-popup-*`：菜单 / 下拉 / 拾色器共用一套
 * 弹层皮肤（兄弟实现见 `np-menu.css` 的 `.np-menu`）。
 *
 * ⚠️ 兜底值只给**读不到**的那几个：`:root` 上的令牌一律不写 ——
 * 兜底是第二份抄本，正是这一轮要消掉的东西（同 `np-menu.css` 的约定）。
 */
.np-color-popover {
  position: fixed;
  z-index: 210;
  border: 1px solid var(--np-popup-border);
  border-radius: var(--np-popup-radius);
  background: var(--np-popup-bg);
  box-shadow: var(--np-popup-shadow);
  /*
   * 玻璃配方靠它：`--np-popup-blur` 是**半径**（0px = 不虚化，等于没这条），
   * 而 `backdrop-filter` 要的是整条函数，所以在这里拼出来。这条声明
   * 不能省 —— 省了之后配方设的值没有任何人读，那个令牌就成了摆设。
   */
  backdrop-filter: blur(var(--np-popup-blur));
  color: var(--np-panel-text);
  /*
   * ⚠️ 字体这一对的兜底是**必需的**：`--np-font` / `--np-font-size` 声明在
   * `.np-designer, .np-node` 上，浮层读不到 —— 不写兜底就掉回系统字体，
   * 和画布里的节点不是一套字。
   */
  font-family: var(--np-font, system-ui, sans-serif);
  font-size: var(--np-font-size, 11px);
}

/*
 * 空值的色块画什么：`swatchColor` 解析不出颜色时用的就是这个令牌。
 * ⚠️ 和 `--np-swatch-alpha` 是一对（那个是「多透明」，这个是「没有颜色时
 * 画什么」）。`.np-widget--color` 的布局在 `theme.css`，这里只补一个令牌 ——
 * 它是色块控件自己的状态色，不是主题的通用色，所以 `var()` 那侧不写兜底：
 * 令牌和引用都在本组件里，兜底只会是同一份值的第二遍。
 */
.np-widget--color {
  --np-swatch-empty: #000000;
}
</style>
